/**
 * Hide and Seek Teams Context
 *
 * The admin's Hide and Seek Teams actions — Team Captains, team assignment,
 * the snake draft, the coin tosses, the Cinematics
 * countdowns — and the results snapshot the server builds for the admin.
 *
 * Mounted by GameTypeProvider (inside HnsProvider, which carries Marco Polo and
 * round events) when the selected game is a Hide and Seek Teams game.
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { onGameStateUpdated, useSocketContext } from '@swtorbit/shared-react-hooks';
import {
  SocketEvent,
  type AdminGetHnstResultsRequest,
  type AdminHnstResultsResponse,
  type Game,
  type GameStateUpdatedBroadcast,
  type HnstActionResponse,
  type HnstCoinChoice,
  type HnstCoinTossKind,
  type HnstCoinTossMode,
  type HnstCountdownConfig,
  type HnstStagePreviewKind,
  type HnstTeamKey,
} from '@swtorbit/shared-socket-events';
import { useGameContext } from './GameContext';

interface HnstContextType {
  setCaptain: (team: HnstTeamKey, playerId: string | null) => Promise<HnstActionResponse>;
  assignPlayer: (playerId: string, team: HnstTeamKey | null) => Promise<HnstActionResponse>;
  configureDraft: (timePerPickSeconds: number) => Promise<HnstActionResponse>;
  startDraft: (firstPick?: HnstTeamKey) => Promise<HnstActionResponse>;
  draftPick: (playerId: string) => Promise<HnstActionResponse>;
  cancelDraft: () => Promise<HnstActionResponse>;
  createCoinToss: (kind: HnstCoinTossKind, mode: HnstCoinTossMode, roundIndexes: number[]) => Promise<HnstActionResponse>;
  updateCoinToss: (tossId: number, mode: HnstCoinTossMode, roundIndexes: number[]) => Promise<HnstActionResponse>;
  deleteCoinToss: (tossId: number) => Promise<HnstActionResponse>;
  flipCoin: (tossId: number) => Promise<HnstActionResponse>;
  chooseForCaptain: (tossId: number, choice: HnstCoinChoice) => Promise<HnstActionResponse>;
  configureCountdowns: (config: HnstCountdownConfig) => Promise<HnstActionResponse>;
  previewCinematic: (targetPlayerId: string, kind: HnstStagePreviewKind) => Promise<HnstActionResponse>;
  hnstAdminResults: AdminHnstResultsResponse | null;
  refreshHnstAdminResults: () => void;
}

const HnstContext = createContext<HnstContextType | undefined>(undefined);

const NOT_CONNECTED: HnstActionResponse = { success: false, error: 'Not connected to the server' };

export function HnstProvider({ children }: { children: ReactNode }) {
  const { socket } = useSocketContext();
  const { selectedGame } = useGameContext();
  const [hnstAdminResults, setHnstAdminResults] = useState<AdminHnstResultsResponse | null>(null);
  const resultsRefreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedGameRef = useRef<Game | null>(selectedGame);
  useEffect(() => {
    selectedGameRef.current = selectedGame;
  }, [selectedGame]);

  /** Emit a request for the selected game and resolve with its ack. */
  const request = useCallback(
    (event: SocketEvent, payload: object): Promise<HnstActionResponse> => {
      const game = selectedGameRef.current;
      if (!socket || !game) return Promise.resolve(NOT_CONNECTED);
      return new Promise((resolve) => {
        socket.emit(event, { roomCode: game.roomCode, ...payload }, (res: HnstActionResponse) => resolve(res ?? NOT_CONNECTED));
      });
    },
    [socket],
  );

  const refreshHnstAdminResults = useCallback(() => {
    const game = selectedGameRef.current;
    if (!game || !socket) return;
    const req: AdminGetHnstResultsRequest = { gameId: game.dbId };
    socket.emit(SocketEvent.HNST_ADMIN_GET_RESULTS, req);
  }, [socket]);

  // Results snapshot: answered on this socket alone. Every change to a live
  // game rebroadcasts it, so refetch (debounced) after each.
  useEffect(() => {
    if (!socket) return;
    const handleResults = (response: AdminHnstResultsResponse) => {
      if (response.gameId !== selectedGameRef.current?.dbId) return;
      setHnstAdminResults(response);
    };
    const handleGameStateUpdated = (broadcast: GameStateUpdatedBroadcast) => {
      const game = selectedGameRef.current;
      if (!game || game.archived || broadcast.game.id !== game.id) return;
      if (resultsRefreshTimer.current) clearTimeout(resultsRefreshTimer.current);
      resultsRefreshTimer.current = setTimeout(() => {
        resultsRefreshTimer.current = null;
        refreshHnstAdminResults();
      }, 250);
    };
    socket.on(SocketEvent.HNST_ADMIN_RESULTS, handleResults);
    const offGameState = onGameStateUpdated(socket, handleGameStateUpdated);
    return () => {
      socket.off(SocketEvent.HNST_ADMIN_RESULTS, handleResults);
      offGameState();
      if (resultsRefreshTimer.current) {
        clearTimeout(resultsRefreshTimer.current);
        resultsRefreshTimer.current = null;
      }
    };
  }, [socket, refreshHnstAdminResults]);

  // A newly selected game starts with its own results.
  useEffect(() => {
    setHnstAdminResults(null);
    refreshHnstAdminResults();
  }, [selectedGame?.dbId, refreshHnstAdminResults]);

  const value = useMemo<HnstContextType>(() => ({
    setCaptain: (team, playerId) => request(SocketEvent.HNST_SET_CAPTAIN, { team, playerId }),
    assignPlayer: (playerId, team) => request(SocketEvent.HNST_ASSIGN_PLAYER, { playerId, team }),
    configureDraft: (timePerPickSeconds) => request(SocketEvent.HNST_CONFIGURE_DRAFT, { timePerPickSeconds }),
    startDraft: (firstPick) => request(SocketEvent.HNST_START_DRAFT, firstPick ? { firstPick } : {}),
    draftPick: (playerId) => request(SocketEvent.HNST_DRAFT_PICK, { playerId }),
    cancelDraft: () => request(SocketEvent.HNST_CANCEL_DRAFT, {}),
    createCoinToss: (kind, mode, roundIndexes) => request(SocketEvent.HNST_CREATE_COIN_TOSS, { kind, mode, roundIndexes }),
    updateCoinToss: (tossId, mode, roundIndexes) => request(SocketEvent.HNST_UPDATE_COIN_TOSS, { tossId, mode, roundIndexes }),
    deleteCoinToss: (tossId) => request(SocketEvent.HNST_DELETE_COIN_TOSS, { tossId }),
    flipCoin: (tossId) => request(SocketEvent.HNST_FLIP_COIN, { tossId }),
    chooseForCaptain: (tossId, choice) => request(SocketEvent.HNST_COIN_CHOICE, { tossId, choice }),
    configureCountdowns: (config) => request(SocketEvent.HNST_CONFIGURE_COUNTDOWNS, config),
    previewCinematic: (targetPlayerId, kind) => request(SocketEvent.ADMIN_PREVIEW_CINEMATIC, { targetPlayerId, kind }),
    hnstAdminResults,
    refreshHnstAdminResults,
  }), [request, hnstAdminResults, refreshHnstAdminResults]);

  return <HnstContext.Provider value={value}>{children}</HnstContext.Provider>;
}

export function useHnstContext() {
  const context = useContext(HnstContext);
  if (context === undefined) {
    throw new Error('useHnstContext must be used within an HnstProvider');
  }
  return context;
}
